import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { cx } from '../components/ui';
import { timecode } from '../lib/format';
import { REACTION_META, clusterLabel, clusterReactions } from '../lib/reactions';
import type { Reaction } from '../lib/types';
import { isPendingReactionId } from './useReactions';

/**
 * Reactions drawn along the scrub bar.
 *
 * This is the "where are the good bits" view. Two reactions a second apart are
 * about the same moment, so they collapse into one marker carrying a count
 * rather than stacking into an unreadable smear. Clicking one seeks, exactly
 * like clicking the timecode on a comment does.
 *
 * The rail sits *above* the scrub track, not on top of it. A marker overlaying
 * the seek control would eat the drag that starts on it, and losing a scrub
 * because you grabbed the bar a pixel too close to a reaction is a bad trade
 * for a decoration.
 *
 * Keyboard: the rail is one tab stop with arrow keys moving between markers,
 * so forty reactions do not become forty tab stops between the play button and
 * the speed control. Arrow keys are stopped from reaching the player's own
 * seek shortcuts while a marker has focus.
 */

export function ReactionMarkers({
  reactions,
  durationSec,
  onSeek,
  className,
  tone = 'overlay',
}: {
  reactions: readonly Reaction[];
  durationSec: number;
  onSeek: (seconds: number) => void;
  className?: string;
  /** `overlay` sits on the dark video chrome; `panel` sits on a card surface. */
  tone?: 'overlay' | 'panel';
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);

  const clusters = useMemo(
    () => clusterReactions(reactions, durationSec, isPendingReactionId),
    [reactions, durationSec],
  );

  // Reactions arriving (or a rollback removing one) must not leave the roving
  // index pointing past the end of the list.
  useEffect(() => {
    setActiveIndex((current) => (current < clusters.length ? current : Math.max(0, clusters.length - 1)));
  }, [clusters.length]);

  // Nothing to mark: take up no space at all, so a recording with no reactions
  // gets exactly the player it had before this existed.
  if (clusters.length === 0 || !Number.isFinite(durationSec) || durationSec <= 0) {
    return null;
  }

  const focusMarker = (index: number) => {
    setActiveIndex(index);
    const rail = railRef.current;
    if (!rail) return;
    const buttons = rail.querySelectorAll<HTMLButtonElement>('[data-reaction-marker]');
    buttons[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    // Space activates the focused marker rather than pausing the video.
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.stopPropagation();
      return;
    }

    let next: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = clusters.length - 1;
    if (next === null) return;

    event.preventDefault();
    // The player listens for arrow keys on `window`. Without this, moving
    // between markers would also seek the video five seconds at a time.
    event.stopPropagation();
    focusMarker(Math.max(0, Math.min(clusters.length - 1, next)));
  };

  return (
    <div
      ref={railRef}
      className={cx('relative', className)}
      role="group"
      aria-label={`Reaction moments, ${clusters.length} on this timeline`}
    >
      {clusters.map((cluster, index) => {
        const percent = Math.max(0, Math.min(100, (cluster.timecodeSec / durationSec) * 100));
        const meta = REACTION_META[cluster.topKind];

        return (
          <button
            key={cluster.key}
            type="button"
            data-reaction-marker
            tabIndex={index === activeIndex ? 0 : -1}
            onFocus={() => setActiveIndex(index)}
            onKeyDown={(event) => onKeyDown(event, index)}
            onClick={() => onSeek(cluster.timecodeSec)}
            title={`${timecode(cluster.timecodeSec)} · ${cluster.count} reaction${cluster.count === 1 ? '' : 's'}`}
            aria-label={clusterLabel(cluster, timecode(cluster.timecodeSec))}
            className={cx(
              'absolute bottom-0 flex -translate-x-1/2 items-center gap-0.5 rounded-full border px-1 py-0.5',
              'leading-none transition-colors duration-120',
              tone === 'overlay'
                ? 'border-chalk/25 bg-slate-950/85 hover:border-signal hover:bg-slate-900'
                : 'border-hairline bg-slate-900 hover:border-signal hover:bg-slate-800',
              cluster.pending && 'opacity-60',
            )}
            style={{ left: `${percent}%` }}
          >
            <span aria-hidden="true" className="text-[9px] leading-none">
              {meta.glyph}
            </span>
            {cluster.count > 1 ? (
              <span aria-hidden="true" className="tnum text-[9px] leading-none text-chalk-dim">
                {cluster.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
